import { useMemo, useState } from "react";
import { LoadErrorState } from "../ui/LoadState";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { CrawlJob } from "../../../../shared/types";
import { SUPPORTED_SOCIAL_PLATFORMS } from "../../../../shared/types";
import { api, ApiError } from "../../lib/api";
import { Badge } from "../ui/Badge";

function statusVariant(status: CrawlJob["status"]): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "done":
      return "success";
    case "partial_success":
      return "warning";
    case "failed":
      return "error";
    case "crawling":
      return "info";
    default:
      return "default";
  }
}

function formatStatus(status: CrawlJob["status"]): string {
  return status.replace(/_/g, " ");
}

function truncateUrl(url: string): string {
  if (url.length <= 40) return url;
  return `${url.slice(0, 37)}...`;
}

function jobSummary(job: CrawlJob): string {
  if (job.type === "web") {
    if (job.pages_attempted) {
      const failed = job.pages_failed ?? 0;
      if (failed > 0) return `${job.pages_attempted - failed}/${job.pages_attempted} pages`;
      return `${job.pages_attempted} pages`;
    }
  }
  return `${job.item_count} chunks`;
}

export function getCrawlJobsRefetchInterval(jobs: CrawlJob[]): number | false {
  return jobs.some((job) => job.status === "queued" || job.status === "crawling") ? 3_000 : false;
}

export function SourcesPanel({
  brandId,
  expanded = true,
  onToggle,
  panelRef,
}: {
  brandId: string;
  expanded?: boolean;
  onToggle?: () => void;
  panelRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const qc = useQueryClient();
  const [socialUrl, setSocialUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Record<string, boolean>>({});

  const jobsQuery = useQuery({
    queryKey: ["brands", brandId, "crawl-jobs"],
    queryFn: () => api.get<{ data: CrawlJob[]; next_cursor?: string }>(`/api/brands/${brandId}/crawl?limit=10`),
    enabled: Boolean(brandId),
    refetchInterval: (query) => getCrawlJobsRefetchInterval(query.state.data?.data ?? []),
  });

  const jobs = jobsQuery.data?.data ?? [];
  const indexedCount = useMemo(
    () => jobs
      .filter((job) => job.status === "done" || job.status === "partial_success")
      .reduce((sum, job) => sum + job.item_count, 0),
    [jobs],
  );

  const createMutation = useMutation({
    mutationFn: (body: { type: "social"; url: string }) =>
      api.post<{ data: CrawlJob }>(`/api/brands/${brandId}/crawl`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands", brandId, "crawl-jobs"] });
      setErrorMessage(null);
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : "Failed to start crawl");
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (jobId: string) =>
      api.post<{ data: CrawlJob }>(`/api/brands/${brandId}/crawl/${jobId}/refresh`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands", brandId, "crawl-jobs"] });
      setErrorMessage(null);
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : "Failed to refresh crawl");
    },
  });

  function submitSocial(rawUrl: string) {
    const url = rawUrl.trim();
    if (!url) return;
    createMutation.mutate({ type: "social", url });
    setSocialUrl("");
  }

  return (
    <section
      ref={panelRef}
      className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm"
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="font-data text-[11px] uppercase tracking-wider text-slate-500">
            Voice Discovery sources
          </div>
          <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
            {jobsQuery.data ? `Sources (${indexedCount} indexed)` : "Sources"}
          </h3>
        </div>
        <span className="text-sm text-slate-500">{expanded ? "Hide" : "Show"}</span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-medium text-slate-900">Website pages</div>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Process a registered website property so the source, coverage, and saved pages stay together.
              </p>
              <Link
                to={`/brand/${brandId}/settings/surfaces`}
                className="mt-3 inline-flex text-sm font-medium text-indigo-600 hover:underline"
              >
                Manage website properties
              </Link>
            </div>

            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                submitSocial(socialUrl);
              }}
            >
              <label className="block text-sm font-medium text-slate-900">Social profile URL</label>
              <div className="flex gap-2">
                <input
                  value={socialUrl}
                  onChange={(e) => setSocialUrl(e.target.value)}
                  placeholder="https://www.instagram.com/nike/"
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                />
                <button
                  type="submit"
                  disabled={createMutation.isPending || !socialUrl.trim()}
                  className="rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Crawl
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Supported: {SUPPORTED_SOCIAL_PLATFORMS.join(", ")}.
              </p>
            </form>
          </div>

          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          {jobsQuery.isLoading ? (
            <div className="text-sm text-slate-500">Loading indexed sources…</div>
          ) : jobsQuery.isError && !jobsQuery.data ? (
            <LoadErrorState compact title="Indexed sources could not be loaded" description="This does not mean nothing is indexed." onRetry={() => void jobsQuery.refetch()} />
          ) : jobs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
              No sources indexed yet. Process websites from Properties or add a supported social profile above.
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => {
                const canRefresh = job.type === "social"
                  && (job.status === "done" || job.status === "partial_success");
                const showErrors = Boolean(job.errors?.length);
                return (
                  <article key={job.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={statusVariant(job.status)}>{formatStatus(job.status)}</Badge>
                          <span className="text-xs uppercase tracking-wide text-slate-500">{job.type}</span>
                        </div>
                        <div className="mt-2 text-sm font-medium text-slate-900" title={job.url}>
                          {truncateUrl(job.url)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {jobSummary(job)}
                          {job.platform ? ` · ${job.platform}` : ""}
                        </div>
                      </div>
                      {job.type === "web" && job.surface_id ? (
                        <Link
                          to={`/brand/${brandId}/settings/surfaces/${job.surface_id}/content`}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          View website pages
                        </Link>
                      ) : canRefresh ? (
                        <button
                          type="button"
                          onClick={() => refreshMutation.mutate(job.id)}
                          disabled={refreshMutation.isPending}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Refresh
                        </button>
                      ) : null}
                    </div>

                    {showErrors && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => setExpandedErrors((current) => ({ ...current, [job.id]: !current[job.id] }))}
                          className="text-xs font-medium text-slate-600 underline decoration-slate-300 underline-offset-2"
                        >
                          {expandedErrors[job.id] ? "Hide errors" : `Show errors (${job.errors?.length ?? 0})`}
                        </button>
                        {expandedErrors[job.id] && (
                          <div className="mt-2 space-y-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                            {job.errors?.map((error, index) => (
                              <div key={`${job.id}-${index}`} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                                <div className="font-medium text-slate-900">{error.target}</div>
                                <div className="mt-1">{error.message}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {createMutation.error instanceof ApiError && createMutation.error.status === 400 && (
            <div className="text-xs text-slate-500">
              Unsupported social URLs are rejected and should match one of the supported platforms above.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
